// Chapter 25 - Differential Reinforcement
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 25.

import type { ChapterModule } from "./types";

const chapter25Module: ChapterModule = {
  meta: {
    id: "25",
    number: 25,
    title: "Differential Reinforcement",
    subtitle: "DRA, DRI, DRO, DRL",
    part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures",
    estMinutes: 70,
    bacb: ["G-14"],
  },
  curated: true,
  overview: [
    "Chapter 25 covers DIFFERENTIAL REINFORCEMENT as a class of nonpunishment procedures for decreasing problem behavior. The four variants studied most: DRA (Alternative behavior), DRI (Incompatible behavior, a special case of DRA), DRO (Other behavior - reinforce the absence of the problem behavior), and DRL (Low rates - reinforce reduced rates without eliminating the behavior).",
    "You will learn how each procedure works as a concurrent schedule, the matching-law rationale behind DRA's effectiveness, interval and momentary DRO variants, full-session/interval/spaced-responding DRL variants, and the guidelines for selecting alternatives, choosing schedules, and combining with other tactics.",
  ],
  objectives: [
    { id: "G-14", text: "Use differential reinforcement procedures (DRA, DRI, DRO, DRL) to decrease problem behavior." },
  ],
  coreConcepts: [
    {
      title: "Differential reinforcement defined",
      body: [
        "Differential reinforcement entails reinforcing one response class and withholding reinforcement for another. As a reductive procedure, differential reinforcement (a) provides reinforcement contingent on a behavior OTHER than the problem behavior (or the problem behavior at a reduced rate) and (b) withholds reinforcement for the problem behavior as much as possible.",
      ],
    },
    {
      title: "DRA: Differential reinforcement of alternative behavior",
      body: [
        "DRA reinforces occurrences of a desirable ALTERNATIVE to the problem behavior and withholds reinforcement for the problem behavior. Conceptualized as a concurrent schedule: alternative behavior on a DENSE schedule of high-quality reinforcement; problem behavior on extinction or a very lean schedule. The MATCHING LAW predicts response allocation will shift toward the alternative.",
      ],
    },
    {
      title: "DRI: Differential reinforcement of incompatible behavior",
      body: [
        "DRI is a SPECIAL CASE of DRA in which the reinforced alternative cannot occur simultaneously with the problem behavior (e.g., hands in lap vs hands hitting). Physical incompatibility provides a structural guarantee that the problem behavior cannot occur during the reinforced behavior.",
      ],
    },
    {
      title: "DRA/DRI selection guidelines",
      body: [
        "Pick alternative behaviors that are (a) already in repertoire, (b) require equal or less effort than the problem behavior, (c) occur frequently enough at baseline to provide reinforcement opportunities, and (d) likely to be reinforced in the natural environment when intervention ends. Use potent reinforcers, start with CRF then thin, combine with other reductive procedures.",
      ],
    },
    {
      title: "DRO: Differential reinforcement of OTHER behavior",
      body: [
        "DRO delivers reinforcers contingent on the problem behavior NOT OCCURRING during a time interval (interval DRO) or at specific moments in time (momentary DRO). With INTERVAL DRO, any instance of the problem behavior during the interval RESETS the interval. With MOMENTARY DRO, only the moment-of-check matters. Both can be on fixed-time or variable-time schedules.",
      ],
    },
    {
      title: "DRO implementation guidelines",
      body: [
        "Set an initial interval that ensures frequent reinforcement at current behavior levels; avoid inadvertently reinforcing other inappropriate behaviors; deliver reinforcement contingent on absence of the problem AND any other significant inappropriate behaviors; increase the interval gradually based on decreases in the problem; extend to other settings; combine with other reductive procedures.",
      ],
    },
    {
      title: "DRL: Differential reinforcement of low rates",
      body: [
        "DRL reinforces (a) each occurrence separated from the previous response by a minimum interresponse time (spaced-responding DRL) or (b) responding within a period not exceeding a predetermined criterion (full-session or interval DRL). Used to DECREASE the rate of a behavior that occurs too frequently, NOT to eliminate it.",
      ],
      bullets: [
        "Full-session DRL: reinforce when the entire session's responses are at or below criterion.",
        "Interval DRL: divide session into intervals; reinforce at the end of each interval when responses were at or below criterion.",
        "Spaced-responding DRL: reinforce each response that is separated from the prior response by at least the minimum IRT.",
      ],
    },
    {
      title: "DRL guidelines and contraindications",
      body: [
        "Do NOT use DRL if rapid reduction is needed or with self-injurious or violent behavior. Pick the DRL variant by goal: full-session or interval DRL when zero rate is acceptable as a goal; spaced-responding DRL when reducing rate of a behavior to be maintained in the repertoire. Use baseline data to set initial limits; thin the schedule gradually; provide feedback to help the learner monitor rate.",
      ],
    },
    {
      title: "Practical tips across procedures",
      body: [
        "Plan for increased occurrences when shifting from clinic to natural settings, when new caregivers join, and when schedules thin. Combine with other reductive procedures. Maintain extinction for the problem behavior whenever feasible.",
      ],
    },
  ],
  glossary: [
    { term: "Differential reinforcement", definition: "Reinforcing one response class while withholding reinforcement for another.", sourceHint: "Differential reinforcement entails reinforcing one response class and withholding reinforcement for another." },
    { term: "Differential reinforcement of alternative behavior (DRA)", definition: "Reinforce a desirable alternative to the problem behavior; withhold reinforcement for the problem behavior.", sourceHint: "A practitioner applying DRA reinforces occurrences of a desirable alternative to the problem behavior and withholds reinforcement for the problem behavior." },
    { term: "Differential reinforcement of incompatible behavior (DRI)", definition: "Special case of DRA in which the alternative cannot occur simultaneously with the problem behavior.", sourceHint: "When DRA entails reinforcing a behavior that cannot occur simultaneously with the problem behavior, the procedure is sometimes called differential reinforcement of incompatible behavior (DRI)." },
    { term: "Differential reinforcement of other behavior (DRO)", definition: "Reinforcement contingent on the problem behavior NOT occurring during a time interval or at a specific moment.", sourceHint: "A practitioner applying DRO delivers reinforcers contingent on the problem behavior not occurring throughout intervals of time (interval DRO) or at specific moments of time (momentary DRO)." },
    { term: "Interval DRO", definition: "Reinforcement contingent on the absence of the problem behavior throughout a predetermined interval; an occurrence resets the interval.", sourceHint: "With interval DRO, the omission of the problem behavior throughout a predetermined time interval produces reinforcement, and any instance of the problem behavior during the interval resets the interval." },
    { term: "Momentary DRO", definition: "Reinforcement contingent on absence of the problem behavior at specific moments in time.", sourceHint: "With momentary DRO, absence of the problem behavior at predetermined moments in time produces reinforcement." },
    { term: "Differential reinforcement of low rates (DRL)", definition: "Reinforcement contingent on a reduced rate of the target behavior; used to lower (not eliminate) behavior.", sourceHint: "DRL is used to decrease the rate of a behavior that occurs too frequently, but not to eliminate the behavior entirely." },
    { term: "Full-session DRL", definition: "Reinforcement delivered when responses during the entire session are equal to or below a criterion limit.", sourceHint: "Reinforcement on a full-session DRL schedule is delivered when responding during an entire instructional or treatment session is equal to or below a criterion limit." },
    { term: "Interval DRL", definition: "Divide the session into intervals; reinforce at the end of each interval when responses were at or below the criterion.", sourceHint: "On an interval DRL schedule of reinforcement, the practitioner divides the total session into a series of equal intervals of time and provides reinforcement at the end of each interval in which the number of responses was equal to or below a criterion limit." },
    { term: "Spaced-responding DRL", definition: "Reinforce each occurrence that is separated from the previous response by a minimum interresponse time.", sourceHint: "Reinforcement on a spaced-responding DRL schedule follows each occurrence of the target behavior that is separated from the previous response by a minimum interresponse time (IRT)." },
    { term: "Matching law", definition: "Response allocation tends to match the proportion of reinforcement obtained across concurrent options.", sourceHint: "The learner allocates more responding to the alternative behavior and less responding to the problem behavior, as described by the matching law." },
  ],
  procedures: [
    {
      title: "Implement a DRA program",
      intro: "Reinforce a desirable alternative on a dense schedule; extinguish the problem behavior.",
      steps: [
        { title: "Select alternative", detail: "Choose a behavior in repertoire requiring equal/less effort that is likely to receive reinforcement in the natural environment." },
        { title: "Choose reinforcers", detail: "Pick potent reinforcers that can be delivered for the alternative and withheld for the problem behavior; the maintaining reinforcer of the problem behavior often works." },
        { title: "Run dense schedule", detail: "Reinforce alternative on CRF initially; place problem behavior on extinction." },
        { title: "Thin", detail: "Gradually thin the reinforcement schedule for the alternative." },
        { title: "Maintain", detail: "Plan for setting changes, new caregivers, and schedule thinning; combine with other procedures." },
      ],
    },
    {
      title: "Implement an interval DRO program",
      intro: "Reinforce absence of the problem behavior during intervals.",
      steps: [
        { title: "Set initial interval", detail: "Use baseline IRT data; pick an interval the learner can reliably meet for first reinforcement opportunities." },
        { title: "Deliver reinforcement on absence", detail: "At the end of each interval with no occurrences, deliver reinforcement." },
        { title: "Reset on occurrence", detail: "Restart the interval if the problem behavior occurs." },
        { title: "Avoid inadvertent reinforcement", detail: "Withhold reinforcement also when other significant inappropriate behaviors are occurring at the interval's end." },
        { title: "Gradually lengthen interval", detail: "Increase as the problem behavior decreases; extend to other settings." },
      ],
    },
    {
      title: "Pick the right DRL variant",
      intro: "Match DRL type to the goal.",
      steps: [
        { title: "Need rapid reduction?", detail: "Do NOT use DRL." },
        { title: "Need to eventually eliminate?", detail: "Full-session or interval DRL." },
        { title: "Need to reduce rate while keeping the behavior?", detail: "Spaced-responding DRL with a minimum IRT criterion." },
        { title: "Set initial criteria from baseline", detail: "Use baseline data to choose a manageable starting limit." },
        { title: "Thin gradually + give feedback", detail: "Tighten the criterion in small steps; provide feedback so the learner can monitor rate." },
      ],
    },
  ],
  examples: [
    { scenario: "Student raises hand to ask questions; teacher praises and answers questions whenever the hand is raised, ignores blurting out.",
      application: "DRA - hand raising as the alternative; blurting on extinction.",
      sourceHint: "A practitioner applying DRA reinforces occurrences of a desirable alternative to the problem behavior and withholds reinforcement for the problem behavior." },
    { scenario: "Child receives a sticker every 5 minutes that contains no instances of throwing toys.",
      application: "Interval DRO with a fixed 5-minute interval.",
      sourceHint: "With interval DRO, the omission of the problem behavior throughout a predetermined time interval produces reinforcement." },
    { scenario: "A learner asks 5 questions in 30 minutes; goal is 1 question per 10 minutes. Reinforcer delivered when responses across the entire session are at or below 3.",
      application: "Full-session DRL.",
      sourceHint: "Reinforcement on a full-session DRL schedule is delivered when responding during an entire instructional or treatment session is equal to or below a criterion limit." },
    { scenario: "Pulling out hair is incompatible with knitting; teacher reinforces knitting in lap when the learner sits.",
      application: "DRI - the alternative (knitting) cannot co-occur with the problem behavior (pulling hair).",
      sourceHint: "When DRA entails reinforcing a behavior that cannot occur simultaneously with the problem behavior, the procedure is sometimes called differential reinforcement of incompatible behavior (DRI)." },
  ],
  comparisons: [
    {
      title: "DRA vs DRI vs DRO",
      left:  { label: "DRA / DRI", points: [
        "Reinforce specified alternative behavior.",
        "DRI: alternative is incompatible with problem behavior.",
        "Problem behavior on extinction.",
      ]},
      right: { label: "DRO", points: [
        "Reinforce ABSENCE of the problem behavior.",
        "Does not specify which other behaviors are reinforced - watch for inadvertent reinforcement.",
        "Interval or momentary variants.",
      ]},
    },
    {
      title: "Interval DRO vs Momentary DRO",
      left:  { label: "Interval DRO", points: [
        "Reinforce when problem behavior is absent THROUGHOUT the interval.",
        "Any occurrence resets the interval.",
      ]},
      right: { label: "Momentary DRO", points: [
        "Reinforce when problem behavior is absent at the END of the interval.",
        "Occurrences during the interval do not affect the next reinforcement opportunity.",
      ]},
    },
    {
      title: "Full-session vs Interval vs Spaced-responding DRL",
      left:  { label: "Full-session / Interval DRL", points: [
        "Reinforce when responses across session (or interval) at or below criterion.",
        "Acceptable goal: zero rate.",
      ]},
      right: { label: "Spaced-responding DRL", points: [
        "Reinforce each occurrence separated by a minimum IRT.",
        "Behavior is maintained at a reduced rate, not eliminated.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "DRO reinforces a specific alternative behavior.",
      reality: "DRO reinforces the ABSENCE of the problem behavior. It does not specify which other behaviors are reinforced - which is why practitioners must watch for inadvertent reinforcement of other inappropriate behaviors.",
      sourceHint: "A practitioner applying DRO delivers reinforcers contingent on the problem behavior not occurring throughout intervals of time (interval DRO) or at specific moments of time (momentary DRO).",
    },
    {
      myth: "DRL is appropriate for self-injurious behavior.",
      reality: "DRL should NOT be used with self-injurious or other violent behaviors, or when rapid reduction is required.",
      sourceHint: "Not use DRL if a problem behavior needs to be reduced quickly. Not use DRL with self-injurious or other violent behaviors.",
    },
    {
      myth: "DRA does not require putting the problem behavior on extinction.",
      reality: "Effective DRA programs withhold reinforcement for the problem behavior whenever feasible; otherwise the matching law predicts continued responding to both options.",
      sourceHint: "DRA can be conceptualized as a concurrent schedule of reinforcement in which two response classes receive reinforcement at different rates: (a) the alternative behavior on a dense schedule of high-quality reinforcement and (b) the problem behavior on extinction or a very lean schedule of reinforcement.",
    },
    {
      myth: "DRI and DRA are different procedures.",
      reality: "DRI is a SPECIAL CASE of DRA in which the alternative is physically incompatible with the problem behavior.",
      sourceHint: "When DRA entails reinforcing a behavior that cannot occur simultaneously with the problem behavior, the procedure is sometimes called differential reinforcement of incompatible behavior (DRI).",
    },
  ],
  examChecklist: [
    "Define differential reinforcement as a reductive procedure.",
    "Distinguish DRA, DRI, DRO, and DRL with one example each.",
    "Describe interval DRO and momentary DRO and one difference.",
    "Distinguish full-session, interval, and spaced-responding DRL.",
    "Apply the matching law to explain why DRA works.",
    "List four criteria for selecting an alternative behavior in DRA.",
    "List four DRO implementation guidelines.",
    "List two contraindications for DRL.",
    "Explain why DRO can inadvertently reinforce other inappropriate behavior.",
  ],
  flashcardTerms: [
    "Differential reinforcement",
    "Differential reinforcement of alternative behavior (DRA)",
    "Differential reinforcement of incompatible behavior (DRI)",
    "Differential reinforcement of other behavior (DRO)",
    "Interval DRO", "Momentary DRO",
    "Differential reinforcement of low rates (DRL)",
    "Full-session DRL", "Interval DRL", "Spaced-responding DRL",
    "Matching law",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which procedure reinforces the ABSENCE of the problem behavior during an interval?",
      options: [
        { label: "A", text: "DRA" },
        { label: "B", text: "DRI" },
        { label: "C", text: "DRO" },
        { label: "D", text: "DRL" },
      ],
      answer: "C",
      explanation: "DRO reinforces absence of the problem behavior across intervals or at moments. DRA and DRI reinforce a specific alternative.",
      topic: "DRO",
      sourceHint: "A practitioner applying DRO delivers reinforcers contingent on the problem behavior not occurring throughout intervals of time (interval DRO) or at specific moments of time (momentary DRO).",
    },
    {
      id: "Q2",
      question: "DRI is best described as:",
      options: [
        { label: "A", text: "A type of DRO with a long interval" },
        { label: "B", text: "A special case of DRA in which the alternative cannot occur simultaneously with the problem behavior" },
        { label: "C", text: "Reinforcement of low rates" },
        { label: "D", text: "An extinction procedure" },
      ],
      answer: "B",
      explanation: "DRI reinforces an alternative that is INCOMPATIBLE with the problem behavior; it is a special case of DRA.",
      topic: "DRI",
      sourceHint: "When DRA entails reinforcing a behavior that cannot occur simultaneously with the problem behavior, the procedure is sometimes called differential reinforcement of incompatible behavior (DRI).",
    },
    {
      id: "Q3",
      question: "Which DRL variant reinforces each occurrence that is separated from the prior by a minimum IRT?",
      options: [
        { label: "A", text: "Full-session DRL" },
        { label: "B", text: "Interval DRL" },
        { label: "C", text: "Spaced-responding DRL" },
        { label: "D", text: "Momentary DRL" },
      ],
      answer: "C",
      explanation: "Spaced-responding DRL uses a minimum IRT to lower rate while preserving the behavior in repertoire.",
      topic: "DRL variants",
      sourceHint: "Reinforcement on a spaced-responding DRL schedule follows each occurrence of the target behavior that is separated from the previous response by a minimum interresponse time (IRT).",
    },
    {
      id: "Q4",
      question: "Which is a contraindication for DRL?",
      options: [
        { label: "A", text: "Behavior occurs at a high rate" },
        { label: "B", text: "Self-injurious or violent behavior" },
        { label: "C", text: "The goal is to reduce rate but keep the behavior" },
        { label: "D", text: "Baseline data are stable" },
      ],
      answer: "B",
      explanation: "DRL should NOT be used with self-injurious/violent behavior or when rapid reduction is required.",
      topic: "DRL contraindications",
      sourceHint: "Not use DRL with self-injurious or other violent behaviors.",
    },
    {
      id: "Q5",
      question: "Which law predicts that response allocation will match reinforcement allocation across concurrent options?",
      options: [
        { label: "A", text: "Premack principle" },
        { label: "B", text: "Matching law" },
        { label: "C", text: "Functional law" },
        { label: "D", text: "Operant law" },
      ],
      answer: "B",
      explanation: "The matching law underlies DRA's design: dense reinforcement for the alternative shifts allocation toward it.",
      topic: "Matching law",
      sourceHint: "The learner allocates more responding to the alternative behavior and less responding to the problem behavior, as described by the matching law.",
    },
    {
      id: "Q6",
      question: "An interval DRO is in effect; the learner emits the problem behavior 30 seconds into a 2-minute interval. What happens?",
      options: [
        { label: "A", text: "The interval continues; if the behavior does not recur, reinforcement is delivered" },
        { label: "B", text: "The interval RESETS" },
        { label: "C", text: "The schedule shifts to spaced-responding DRL" },
        { label: "D", text: "Reinforcement is delivered immediately" },
      ],
      answer: "B",
      explanation: "Interval DRO resets the interval on any occurrence of the problem behavior.",
      topic: "Interval DRO",
      sourceHint: "With interval DRO, the omission of the problem behavior throughout a predetermined time interval produces reinforcement, and any instance of the problem behavior during the interval resets the interval and postpones reinforcement.",
    },
  ],
  reviewPrompts: [
    "Design a DRA program for an attention-maintained behavior including the alternative behavior, schedule, and extinction plan.",
    "Distinguish DRO interval vs momentary variants with a real-world example.",
    "Pick the right DRL variant for two different goals (eliminate vs reduce-but-preserve).",
    "Explain how the matching law predicts DRA effectiveness.",
    "List four guidelines for selecting alternatives in DRA.",
  ],
  recap: [
    "Differential reinforcement reinforces one response class while withholding reinforcement for another. DRA reinforces an alternative; DRI reinforces an incompatible alternative; DRO reinforces absence of the problem; DRL reinforces lowered rates.",
    "DRA/DRI are concurrent schedules: dense reinforcement for the alternative + extinction for the problem behavior. Matching law predicts response allocation shift. DRO comes in interval and momentary variants; interval DRO resets on occurrence.",
    "DRL has full-session, interval, and spaced-responding variants. Spaced-responding DRL preserves behavior at lower rate; full-session/interval DRL can target zero rate. Do not use DRL when rapid reduction is needed or with self-injurious/violent behavior. Combine these procedures with other interventions and plan for setting and caregiver changes.",
  ],
  sourceExcerpts: [
    {
      heading: "DRA and matching law",
      text: "A practitioner applying DRA reinforces occurrences of a desirable alternative to the problem behavior and withholds reinforcement for the problem behavior. DRA can be conceptualized as a concurrent schedule of reinforcement... The learner allocates more responding to the alternative behavior and less responding to the problem behavior, as described by the matching law.",
    },
    {
      heading: "DRO variants",
      text: "A practitioner applying DRO delivers reinforcers contingent on the problem behavior not occurring throughout intervals of time (interval DRO) or at specific moments of time (momentary DRO). With interval DRO, the omission of the problem behavior throughout a predetermined time interval produces reinforcement, and any instance of the problem behavior during the interval resets the interval and postpones reinforcement.",
    },
    {
      heading: "DRL variants",
      text: "Reinforcement on a full-session DRL schedule is delivered when responding during an entire instructional or treatment session is equal to or below a criterion limit. Reinforcement on a spaced-responding DRL schedule follows each occurrence of the target behavior that is separated from the previous response by a minimum interresponse time (IRT).",
    },
    {
      heading: "DRL contraindications",
      text: "When using DRL, practitioners should not use DRL if a problem behavior needs to be reduced quickly. Not use DRL with self-injurious or other violent behaviors.",
    },
  ],
};

export default chapter25Module;
